<template>
  <div v-if="review" ref="reviewContainer" class="isolate h-full w-full">
    <ReviewCard
      ref="reviewCard"
      v-bind="ariaLabel ? { 'aria-label': ariaLabel } : {}"
      :card-class="
        tw`w-full m-auto cursor-pointer bg-float-default-low rounded-lg`
      "
      defer
      :image-attributes="{
        loading: 'lazy',
        class: cardClassName ?? '',
        sizes: isInMainContainer ? MAIN_IMG_SIZES : ALT_IMG_SIZES_LARGE,
      }"
      :modal-name="resolvedModalName"
      overlay-class="hidden"
      :review
      :review-picture-class="tw`m-auto`"
    >
      <template #card-header>
        <RevTag
          class="absolute left-20 top-20"
          data-allow-mismatch
          :label="reviewDate"
          variant="secondary"
        />
      </template>

      <template #card-body>
        <div
          class="mood-inverse absolute bottom-0 left-0 right-0 flex flex-col gap-8"
        >
          <div class="isolate z-[1] flex justify-between gap-8 px-20 pt-20">
            <RevRating :score="review.averageRate" size="extra-small" />
            <span
              class="text-static-default-hi caption-bold ml-auto mr-0 flex items-center"
            >
              <span>
                {{ review.customer?.firstName }}
                {{ review.customer?.lastName }}
              </span>
              <IconOfficialFilled class="ml-4" size="16" />
            </span>
          </div>
          <div
            class="text-static-default-hi caption isolate z-[1] flex flex-col gap-4 px-20 pb-20"
          >
            <p class="line-clamp-4 w-full whitespace-normal text-start">
              {{ review.comment }}
            </p>
            <p
              class="caption-bold w-fit text-start underline"
              variant="primary"
            >
              {{ i18n(translations.viewReview) }}
            </p>
          </div>
          <div
            class="rounded-b-lg absolute bottom-0 left-0 right-0 z-0 h-full"
            :class="
              tw`
            bg-[linear-gradient(180deg,rgba(15,17,23,0)_0%,rgba(15,17,23,0.56)_30.29%,rgba(15,17,23,0.6)_100%)]
            after:content-[''] after:absolute after:bottom-0 after:left-0 after:right-0 after:!top-[initial]
            after:h-full after:pointer-events-none after:backdrop-blur-[16px]
            after:[mask:linear-gradient(180deg,transparent_0px,#000_24px,#000)]
            after:rounded-bl-[0.75rem] after:rounded-br-[0.75rem]`
            "
          />
        </div>
      </template>

      <template #card-footer>{{
        // Passing empty string to override default slot behaviour
        ''
      }}</template>
    </ReviewCard>
  </div>
</template>

<script lang="ts" setup>
import { computed, useTemplateRef } from 'vue'

import { formatDifference } from '@backmarket/nuxt-layer-cms/DynamicTextDates.utils'
import ReviewCard from '@backmarket/nuxt-layer-cms/ReviewCard.vue'
import type { Review } from '@backmarket/nuxt-layer-cms/ReviewsView.vue'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useI18nLocale } from '@backmarket/nuxt-module-i18n/useI18nLocale'
import { RevRating } from '@ds/components/Rating'
import { RevTag } from '@ds/components/Tag'
import { IconOfficialFilled } from '@ds/icons/IconOfficialFilled'
import { tw } from '@ds/utils/tailwind'
import { useIntersectionObserver } from '@vueuse/core'

import { ALT_IMG_SIZES_LARGE, MAIN_IMG_SIZES } from '../../GalleryNew.constants'

import translations from './Review.translations'

const {
  review,
  isInMainContainer = false,
  modalName,
} = defineProps<{
  review: Review
  cardClassName?: string | string[]
  isInMainContainer?: boolean
  modalName?: string
  ariaLabel?: string
}>()

const emit = defineEmits<{
  (e: 'visible'): void
}>()

const reviewCard = useTemplateRef('reviewCard')
const openModal = () => reviewCard.value?.reviewCard?.$el?.click()

defineExpose({
  openModal,
})

const resolvedModalName = computed(() => {
  if (modalName) {
    return modalName
  }

  return `REVIEW_IN_IMAGE_CAROUSEL_${review.id}`
})

const i18n = useI18n()

const currentLocale = useI18nLocale()

const reviewDate = computed(() => {
  const differenceInDays =
    (new Date(review.reviewDate).getTime() - new Date().getTime()) /
    (1000 * 60 * 60 * 24)

  return formatDifference(differenceInDays, currentLocale)
})

const reviewContainer = useTemplateRef('reviewContainer')
const { stop } = useIntersectionObserver(
  reviewContainer,
  ([{ isIntersecting, intersectionRatio }]) => {
    if (isIntersecting && intersectionRatio >= 0.7) {
      emit('visible')
      stop()
    }
  },
  {
    threshold: 0.7,
  },
)
</script>
